import * as React from 'react'
import bind from 'bind-decorator'
import './index.scss'
import { CognitoUser } from '@aws-amplify/auth'
import { Grid, ImageUpload, Block, DictionaryValue } from '../../../components'
import { Button, Form, Alert, Table, Divider } from 'antd'
import { FormComponentProps } from 'antd/lib/form'
import { handleError } from '../../../utilities'
import { API, graphqlOperation } from 'aws-amplify'
import { GraphQLResult } from '@aws-amplify/api/lib/types'
import { createUserUploadedFiles, deleteUserUploadedFiles } from '../../../graphql/mutations'
import moment from 'moment'
import { listUserUploadedFiless } from '../../../graphql/queries'
import { PAGE_SIZE, DATE_FORMAT_TABLE } from '../../../constants'
import { File } from './UploadPageWithData'
import { FileInfo } from '@uploadcare/react-widget'
import { renderAllData } from '../../../utilities/graphql'

type FilesTable = {
    id: string
    fileName: string
    url: string
    uploadedAt: string
}

type Props = {
    currentUser: CognitoUser
    username: string
} & FormComponentProps

type State = {
    error?: Error
    showWidget: boolean
    isSaving: boolean
    fileInfo: any
    success: boolean
    data: FilesTable[]
    loading: boolean
}

class UploadPage extends React.Component<Props, State> {
    readonly state: State = {
        showWidget: false,
        isSaving: false,
        fileInfo: undefined,
        success: false,
        data: [],
        loading: true,
    }

    @bind
    handleClick() {
        this.setState({ showWidget: !this.state.showWidget })
    }

    @bind
    onFinish(fileInfo: FileInfo) {
        this.setState({ fileInfo })
    }

    handleSubmit = (e: any) => {
        e.preventDefault()

        const { username, form: { validateFields, getFieldsValue, setFieldsValue } } = this.props
        const { fileInfo } = this.state
        validateFields(async (err) => {
            this.setState({ isSaving: true })

            const values = getFieldsValue()
            const url = values.files

            if (!err && url && fileInfo) {
                try {
                    const response = await API.graphql(
                        graphqlOperation(createUserUploadedFiles, {
                            input:{
                                url,
                                id: fileInfo.uuid,
                                userId: username,
                                name: fileInfo.name,
                                uploadedAt: moment(),
                            },
                        })
                    )
                    if (response.data && response.data !== null) {
                        const data = this.state.data
                        data.push({
                            id: response.data.createUserUploadedFiles.id,
                            fileName: response.data.createUserUploadedFiles.name,
                            url: response.data.createUserUploadedFiles.url,
                            uploadedAt: moment(response.data.createUserUploadedFiles.uploadedAt)
                                        .format(DATE_FORMAT_TABLE),
                        })
                        this.setState({ data, success: true })
                        setTimeout(() => {
                            this.setState({ success: false })
                        },         3000)
                    } else {
                        handleError(response.errors)
                    }
                } catch (ex) {
                    handleError(ex)
                } finally {
                    setFieldsValue({
                        files: null,
                    })

                    this.setState({ isSaving: false, showWidget: false })
                }
            }
        })
    }

    handleDelete = (id: string) => (e: React.MouseEvent) => {
        e.preventDefault()
        this.setState({ loading: true })

        API.graphql(
            graphqlOperation(deleteUserUploadedFiles, {
                input:{ id },
            })
        ).then((response: GraphQLResult) => {
            if (response.data && response.data !== null) {
                const data = this.state.data.filter((file) => {
                    return file.id !== id
                })
                this.setState({ data, loading: false })
            } else {
                handleError(response.errors)
            }
        })
    }

    async componentDidMount() {
        const data: FilesTable[] = []

        const params = {
            userId: { eq: this.props.username },
        }

        const allFiles: File[] = await renderAllData([], undefined, 'listUserUploadedFiless', params)

        if (allFiles.length > 0) {
            allFiles.map((file: File) => {
                data.push({
                    id: file.id,
                    fileName: file.name,
                    url: file.url,
                    uploadedAt: moment(file.uploadedAt).format(DATE_FORMAT_TABLE),
                })
            })
        }

        this.setState({ data, loading: false })
    }

    render() {
        const { form: { getFieldDecorator } } = this.props

        const { showWidget, isSaving, success, data, loading } = this.state

        const UploadHelp = (
            <Block className='cfa-image-upload__help'>
              <p>File requirements:</p>
              <ul>
                <li>Upload your receipt as a word, pdf or image file (JPG or PNG)</li>
                <li>Accepted formats: DOC, DOCX, PDF, XLS, XLSX, PNG, JPG, JPEG, TXT, RTF, WEBP, HEIF, HEIC</li>
                <li>Maximum 100MB file size</li>
              </ul>
            </Block>
        )

        const allowFileTypes = [
            'doc', 'docx', 'pdf', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'txt', 'rtf', 'webp', 'heif', 'heic',
        ]

        const allowFileSize = {
            max: 100, // Size unit is MB
        }

        const columns = [
            {
                title: 'File Name',
                key: 'fileName',
                width: '50%',
                sorter: (a: any, b: any) => +(a.fileName > b.fileName) - +(a.fileName < b.fileName),
                render: (record: FilesTable) => <a href={record.url} target='_blank'>{record.fileName}</a>,
            },
            {
                title: 'Uploaded Time',
                dataIndex: 'uploadedAt',
                key: 'uploadedAt',
                width: '30%',
                sorter: (a: any, b: any) => +(a.uploadedAt > b.uploadedAt) - +(a.uploadedAt < b.uploadedAt),
            },
            {
                title: '',
                key: 'action',
                width: '20%',
                render: (record: FilesTable) => (
                    <span>
                        <a href={record.url} download={true} target='_blank'>View</a>
                        <Divider type='vertical' />
                        <a onClick={this.handleDelete(record.id)}>Delete</a>
                    </span>
                ),
            },
        ]

        return (
            <Grid className='cf-file-upload'>
                <h1><DictionaryValue token='fileupload.title' /></h1>
                {success &&
                    <Alert
                        message='Your file has been uploaded successfully!'
                        type='success'
                        showIcon={true}
                        closable={true}
                    />
                }
                <div className='cf-file-upload__widget'>
                    <Button
                        type='primary'
                        onClick={this.handleClick}
                        size='large'
                        icon={showWidget ? 'close' : 'upload'}
                    >
                        {showWidget ? 'Cancel' : 'Upload Files'}
                    </Button>
                    {showWidget &&
                    <Form layout='vertical' onSubmit={this.handleSubmit}>
                        <Form.Item label='Upload Files'>
                          {getFieldDecorator('files', {
                              rules: [{ required: true }],
                          })(
                            <ImageUpload
                                resizeWidth={750}
                                resizeHeight={750}
                                description='Please read the file requirements below'
                                allowFileTypes={allowFileTypes}
                                allowFileSize={allowFileSize}
                                onFinish={this.onFinish}
                            />
                          )}
                        </Form.Item>
                        <Form.Item help={UploadHelp}>
                            <Button
                                type='primary'
                                block={true}
                                htmlType='submit'
                                size='large'
                                loading={isSaving}
                            >
                                Upload
                            </Button>
                        </Form.Item>
                    </Form>
                    }
                </div>
                <div className='cf-file-upload__fileslist'>
                    <Table
                        columns={columns}
                        rowKey='id'
                        dataSource={data}
                        loading={loading}
                        pagination={data && data.length < PAGE_SIZE ? false : {
                            defaultPageSize: PAGE_SIZE,
                        }}
                    />
                </div>
            </Grid>
        )
    }
}

export default Form.create<Props>({ name: 'uploadPage' })(UploadPage)
